import StorageManager from '../utils/StorageManager';
import { API_BASE_URL } from '../config/environment';

export class AuthService {
    static getToken() {
        return StorageManager.safeGetItem('token', 'session') || 
               sessionStorage.getItem('token');
    }

    static async fetchWithAuth(endpoint, options = {}) {
        const token = this.getToken();
        
        if (!token) {
            throw new Error('Brak tokenu autoryzacji');
        }

        const defaultOptions = {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            // Dodać timeout
            signal: AbortSignal.timeout(15000) // 15 sekund
        };

        try {
            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                ...defaultOptions,
                ...options,
                headers: {
                    ...defaultOptions.headers,
                    ...options.headers
                }
            });

            // Najpierw sprawdź status odpowiedzi
            if (response.status === 401 || response.status === 403) {
                await this.logout();
                window.location.href = '/';
                throw new Error('Sesja wygasła');
            }

            // Sprawdź typ odpowiedzi
            const contentType = response.headers.get('content-type');
            let data;
            
            if (contentType && contentType.includes('application/json')) {
                try {
                    data = await response.json();
                } catch (parseError) {
                    console.error('JSON parsing error:', parseError);
                    data = { message: 'Błąd przetwarzania odpowiedzi JSON' };
                }
            } else {
                // Dla non-JSON odpowiedzi
                const text = await response.text();
                data = { message: text };
                
                // Jeśli operacja się powiodła (status 2xx) ale nie jest JSONem,
                // traktujemy to jako sukces
                if (response.ok) {
                    return data;
                }
            }

            if (!response.ok && data.requireLogout) {
                await this.logout();
                window.location.href = '/';
                throw new Error(data.message || 'Sesja wygasła');
            }

            if (!response.ok) {
                throw new Error(data.message || 'Wystąpił błąd');
            }

            return data;
        } catch (error) {
            if (error.message.includes('Sesja wygasła') || error.message.includes('Token wygasł')) {
                await this.logout();
                window.location.href = '/';
            }
            throw error;
        }
    }

    static async verifyToken() {
        try {
            const token = this.getToken();
            if (!token) {
                return false;
            }

            await this.fetchWithAuth('/api/verify-session');
            return true;
        } catch (error) {
            console.error('Token verification error:', error);
            
            if (error.message.includes('401') || 
                error.message.includes('403') ||
                error.message.includes('Sesja wygasła') ||
                error.message.includes('Token wygasł')) {
                await this.logout();
            }
            
            return false;
        }
    }

    static _isLoggingOut = false;

    static async logout() {
        if (this._isLoggingOut) return;
        this._isLoggingOut = true;
        
        try {
            const token = this.getToken();
            if (!token) {
                return;
            }

            try {
                // Użyj prawidłowej ścieżki i metody POST
                await this.fetchWithAuth('/api/logout', {
                    method: 'POST'
                });
            } catch (error) {
                // Logujemy błąd, ale kontynuujemy czyszczenie
                console.error('Logout request error:', error);
            }

            // Zawsze czyścimy dane lokalne, niezależnie od odpowiedzi serwera
            StorageManager.safeRemoveItem('token');
            StorageManager.safeRemoveItem('username');
            StorageManager.safeRemoveItem('userRole');
            
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('username');
            sessionStorage.removeItem('userRole');

            window.dispatchEvent(new CustomEvent('userLoggedOut'));
            return true;
        } catch (error) {
            console.error('Logout error:', error);
            // Czyścimy dane nawet w przypadku błędu
            StorageManager.safeRemoveItem('token');
            StorageManager.safeRemoveItem('username');
            StorageManager.safeRemoveItem('userRole');
            sessionStorage.clear();
            throw error;
        }finally {
            this._isLoggingOut = false;
        }

    }

    static isAuthenticated() {
        return !!this.getToken();
    }

    static _authCheckInterval = null;

    static initAuthCheck(intervalMinutes = 5) {
        // Wyczyść poprzedni interval
        if (this._authCheckInterval) {
            clearInterval(this._authCheckInterval);
        }
        
        this.verifyToken();
        this._authCheckInterval = setInterval(() => {
            this.verifyToken();
        }, intervalMinutes * 60 * 1000);
        
        return this._authCheckInterval;
    }

    static handleAuthResponse(response) {
        if (response.status === 401 || response.status === 403) {
            this.logout();
            window.location.href = '/';
            return false;
        }
        return true;
    }

    static async saveAuthData(data) {
        if (StorageManager.hasConsent('session')) {
            StorageManager.safeSetItem('token', data.token, 'session');
            StorageManager.safeSetItem('username', data.username, 'session');
            if (data.userRole) {
                StorageManager.safeSetItem('userRole', data.userRole, 'session');
            }
        } else {
            sessionStorage.setItem('token', data.token);
            sessionStorage.setItem('username', data.username);
            if (data.userRole) {
                sessionStorage.setItem('userRole', data.userRole);
            }
        }
    }
}